import React from 'react'

import { ContributionSummary, ProductsList } from '@components/common'
import { ProductItemGroupedDetails } from '@components/common/ProductItemGroupedDetails/ProductItemGroupedDetails'
import type { TagProps } from '@fourthwall/components'
import { ButtonClean, PriceString, Tag } from '@fourthwall/components'
import { pluralize } from '@fourthwall/utils/dist/string'
import { YOU_FULFILL } from '@modules/Dashboard/Product/Product.consts'

import { TimezonedDateString } from '../../../../../../../../../Timezone/components/TimezonedDateString'
import type { OrderShipment } from '../../Order.types'
import * as S from './ShipmentItem.styled'

type ShipmentItemProps = {
  shipment: OrderShipment
  createdAt: string
  deliveryIndex: number
  allDeliveriesCount: number
}

const titleDateFormat = 'MMM D, YYYY'

export const ShipmentItem = ({
  shipment,
  createdAt,
  deliveryIndex,
  allDeliveriesCount,
}: ShipmentItemProps) => {
  const isSelfFulfilled = YOU_FULFILL.includes(shipment.fulfillmentService)
  const isDigital = shipment.fulfillmentService === 'DIGITAL_ITEM'
  const isGiftCard = shipment.fulfillmentService === 'GIFT_CARD'

  const statusMap: Record<
    OrderShipment['status'],
    { label: TagProps['label']; appearance: TagProps['appearance'] }
  > = {
    NOT_SHIPPED: {
      label: 'Not shipped',
      appearance: 'default',
    },
    SHIPPED: {
      label: 'Shipped',
      appearance: 'success',
    },
    DELIVERED: {
      label: 'Delivered',
      appearance: 'success',
    },
  }

  const getDescription = () => {
    if (isDigital || isGiftCard) return null

    if (shipment.status === 'NOT_SHIPPED' && isSelfFulfilled) {
      return 'To be shipped by you'
    }

    if (shipment.status === 'NOT_SHIPPED' && !isSelfFulfilled) {
      return 'Fulfilled by Fourthwall'
    }

    return (
      <S.ShippedLabel>
        {shipment.reshipment && (
          <>
            <span>Reshipment</span>
            <span> | </span>
          </>
        )}

        {!shipment.reshipment && allDeliveriesCount > 1 && (
          <>
            <span>
              Delivery {deliveryIndex} of {allDeliveriesCount}
            </span>
            <span> | </span>
          </>
        )}

        <span>{isSelfFulfilled ? 'Shipped by you' : 'Shipped by Fourthwall'}</span>
      </S.ShippedLabel>
    )
  }

  const renderDeliveryDate = () => {
    if ('dateFrom' in shipment.deliveryStatus && 'dateTo' in shipment.deliveryStatus) {
      return (
        <>
          <TimezonedDateString date={shipment.deliveryStatus.dateFrom} format={titleDateFormat} /> -{' '}
          <TimezonedDateString date={shipment.deliveryStatus.dateTo} format={titleDateFormat} />
        </>
      )
    }

    if (`date` in shipment.deliveryStatus) {
      return <TimezonedDateString date={shipment.deliveryStatus.date} format={titleDateFormat} />
    }

    return null
  }

  const getTitle = () => {
    if (isDigital) return `Digital ${pluralize(shipment.offers.length, 'item')}`

    if (isGiftCard) return `Gift ${pluralize(shipment.offers.length, 'card')}`

    switch (shipment.deliveryStatus.type) {
      case 'DeliveredAt':
        return <>Delivered on {renderDeliveryDate()}</>
      case 'DeliveryEstimationDateRange':
        return <>Estimated delivery {renderDeliveryDate()}</>
      case 'ShippedOn':
        return <>Shipped on {renderDeliveryDate()}</>
      case 'ArrivesOn':
        return <>Arrives on {renderDeliveryDate()}</>
      case 'ArrivesBetween':
        return <>Arrives {renderDeliveryDate()}</>
      case 'Unknown':
        return (
          <>
            Order placed on <TimezonedDateString date={createdAt} format={titleDateFormat} />
          </>
        )
    }
  }

  const getSubtitle = () => {
    if (shipment.shipmentTracker?.trackingCode) {
      return (
        <>
          Tracking number:{' '}
          {shipment.shipmentTracker?.url ? (
            <ButtonClean
              label={shipment.shipmentTracker?.trackingCode}
              target="_blank"
              href={shipment.shipmentTracker?.url}
            />
          ) : (
            <span>{shipment.shipmentTracker?.trackingCode}</span>
          )}
        </>
      )
    }
  }

  const renderOffer = ({
    variant,
    offer,
    quantity,
    price,
    grouped,
  }: OrderShipment['offers'][number]) => (
    <ProductsList.Item
      key={variant?.id}
      image={offer.thumbnailImage}
      offerId={offer?.id}
      name={offer?.name}
      type={offer?.offerType === 'GIFT_CARD' ? 'giftCard' : undefined}
      variants={variant.attributesDescription}
      details={grouped ? <ProductItemGroupedDetails groupedDetails={grouped} /> : undefined}
      quantity={quantity}
      price={<PriceString value={price.value} />}
    />
  )

  return (
    <ContributionSummary
      description={getDescription()}
      title={getTitle()}
      subtitle={getSubtitle()}
      status={<Tag {...statusMap[shipment.status]} />}
    >
      <ContributionSummary.Section>
        <ProductsList size="large">{shipment.offers?.map(renderOffer)}</ProductsList>
      </ContributionSummary.Section>
    </ContributionSummary>
  )
}
